import React, { useState, useEffect, useRef } from 'react';

import CommonImage from '@components/CommonImage';

import { getInputValue, formatNumericValue, parseInputValue } from '../utils';

import DisplayCost from './DisplayCost';

const WashTable = ({ data = [], totalCost = 0, onChange, isEditable = true, validationErrors = {} }) => {
	const [washes, setWashes] = useState([]);
	const isInitialMount = useRef(true);
	const prevDataRef = useRef(null);

	useEffect(() => {
		const dataString = JSON.stringify(data);
		const prevDataString = JSON.stringify(prevDataRef.current);

		if (dataString === prevDataString) {
			return;
		}

		if (data && Array.isArray(data) && data.length > 0) {
			const newWashes = data.map((item, index) => ({
				id: `wash-${index}`,
				washType: item.wash_type || '',
				cost: formatNumericValue(item.wash_price_usd)
			}));
			setWashes(newWashes);
		} else {
			setWashes([
				{
					id: 'wash-0',
					washType: '',
					cost: ''
				}
			]);
		}

		prevDataRef.current = data;
		if (isInitialMount.current) {
			isInitialMount.current = false;
		}
	}, [data]);

	const transformToApiFormat = (updatedWashes) => {
		return updatedWashes.map((wash) => {
			const { washType, cost, id, wash_changed } = wash;
			return {
				wash_type: washType || '',
				wash_price_usd: formatNumericValue(cost),
				id,
				wash_changed
			};
		});
	};

	const notifyChange = (updatedWashes) => {
		if (onChange && !isInitialMount.current) {
			onChange(transformToApiFormat(updatedWashes));
		}
	};

	const handleAddWash = () => {
		const newWash = {
			id: `wash-${Date.now()}`,
			washType: '',
			cost: ''
		};
		const updatedWashes = [...washes, newWash];
		setWashes(updatedWashes);
		notifyChange(updatedWashes);
	};

	const handleRemoveWash = (id) => {
		if (washes.length > 1) {
			const updatedWashes = washes.filter((w) => w.id !== id);
			setWashes(updatedWashes);
			notifyChange(updatedWashes);
		}
	};

	const handleInputChange = (washId, field, value) => {
		const isTextField = field === 'washType';
		const updatedValue = parseInputValue(value, !isTextField);
		const updatedWashes = washes.map((w) =>
			w.id === washId ? { ...w, [field]: updatedValue, wash_changed: isTextField } : w
		);
		setWashes(updatedWashes);
		notifyChange(updatedWashes);
	};

	return (
		<div className="single-table costing-table-content mt-5">
			<div className="costing-table scroll-x-label">
				<table className="table no-hover">
					<thead>
						<tr>
							<th>Wash Cost Metrics</th>
							{washes.map((wash, index) => (
								<th key={wash.id}>
									<div className="d-flex align-items-center justify-content-between">
										<p>Wash {index + 1}</p>
										{washes.length > 1 && isEditable && (
											<CommonImage
												src="/icons/chip-close.svg"
												alt="remove"
												width={20}
												height={20}
												className="cursor-pointer"
												onClick={() => handleRemoveWash(wash.id)}
											/>
										)}
									</div>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>
								<p className="regular-12 gray_dark_02">Wash Type</p>
							</td>
							{washes.map((wash, index) => {
								const errorKey = `wash-${index}-washType`;
								const hasError = validationErrors[errorKey];
								return (
									<td key={wash.id}>
										<input
											type="text"
											value={wash.washType || ''}
											onChange={(e) =>
												handleInputChange(wash.id, 'washType', e.target.value)
											}
											readOnly={!isEditable}
											className={hasError ? 'common-border-error' : ''}
										/>
									</td>
								);
							})}
						</tr>
						<tr>
							<td>
								<p className="regular-12 gray_dark_02">Cost ($)</p>
							</td>
							{washes.map((wash) => (
								<td key={wash.id}>
									<input
										type="number"
										step="0.01"
										value={getInputValue(wash.cost)}
										onChange={(e) => handleInputChange(wash.id, 'cost', e.target.value)}
										readOnly={!isEditable}
									/>
								</td>
							))}
						</tr>
						<tr className="cost-total-row">
							<td>
								<p className="semibold-14 gray_dark_01">Total Wash Cost</p>
							</td>
							<td colSpan={washes.length}>
								<p className="semibold-14 gray_dark_01">
									<DisplayCost totalCost={totalCost} />
								</p>
							</td>
						</tr>
					</tbody>
				</table>

				{isEditable && (
					<div className="add-more-button w-100">
						<button className="button gray-outline size32 w-100" onClick={handleAddWash}>
							<CommonImage src="/icons/plus-20.svg" alt="add" width={20} height={20} />
							<span className="ml-1 regular-14 gray_dark_01">Add Wash</span>
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

export default WashTable;
